// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useMemo, useState } from 'react';

import { useHoleFeatures } from '@/features/course-trace/useHoleFeatures';
import { resolveRoundCourseId } from '@/services/courseTraceUpload';

import {
  bendsAlongLine,
  holeSelectionContext,
  playingLineFor,
  shapesNearHole,
  type HoleLine,
  type HoleShape,
} from './holeShapes';
import { distanceMeters, type LatLon } from './tileMath';

/** A tee box, carrying its course name ("Yellow") when one is known. */
export type TeePoint = LatLon & { label: string | null };

export type HoleViewGeometry = {
  /** Every derived tee box on this hole, lowest ordinal first. */
  tees: TeePoint[];
  /** Derived green centre, or null when the course has not been mapped that far. */
  green: LatLon | null;
  /**
   * Where the hole bends, in playing order — empty on a hole that plays straight, or that
   * nobody has mapped either way.
   *
   * The turning points an admin placed, where there are any; otherwise the bends of the hole's
   * stored playing line (see `bendsAlongLine`), drawn by OSM or derived from the fairway.
   */
  turns: LatLon[];
  /**
   * The mapped boundaries reaching into this hole's frame, in draw order.
   *
   * Empty on a course the satellite analysis has not run over, which is the ordinary case
   * rather than an error — the view falls back to plain imagery.
   */
  shapes: HoleShape[];
  /** This hole's out-of-bounds lines, as the stakes an admin recorded put them. */
  lines: HoleLine[];
  loading: boolean;
  /** True once the lookups finished and the hole has no green to frame. */
  unavailable: boolean;
  /** The round's course, once resolved. Lets the modal link to course-level admin views. */
  courseId: string | null;
};

export type UseHoleViewGeometryParams = {
  apiBaseUrl: string;
  token: string | null | undefined;
  roundId: string | null;
  holeNumber: number;
  /** Nothing is fetched until the view is actually opened. */
  enabled: boolean;
};

/** Course lookup result, tagged with the round it belongs to so a round change resets it. */
type CourseLookup = { roundId: string; courseId: string | null };

/** What {@link useCourseHoleViewGeometry} reads for a course that is already known. */
export type CourseHoleViewGeometry = Omit<HoleViewGeometry, 'unavailable' | 'courseId'>;

/**
 * One hole's geometry exactly as a player is served it, for a course that is already known.
 *
 * The round's hole view resolves its course first (see {@link useHoleViewGeometry}); the admin
 * hole map knows its course outright and uses this to preview what that view will draw. Both go
 * through here, so the preview cannot drift from the real thing. Served rows only — never the
 * admin's `includeUnserved` read — because a suggestion below the confidence floor is exactly
 * what a player does not get. Nothing is fetched while `courseId` is null.
 */
export const useCourseHoleViewGeometry = (
  apiBaseUrl: string,
  token: string | null | undefined,
  courseId: string | null,
  holeNumber: number
): CourseHoleViewGeometry => {
  const { features, shapes, centrelines, lines, loading } = useHoleFeatures(apiBaseUrl, token, courseId);

  const geometry = useMemo(() => {
    const forHole = features.filter((feature) => feature.holeNumber === holeNumber);
    const tees = forHole
      .filter((feature) => feature.kind === 'tee')
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((feature) => ({ lat: feature.lat, lon: feature.lon, label: feature.name }));
    // Greens carry ordinal 0; if a course ever derives more than one, the most trusted wins.
    const greenFeature = forHole
      .filter((feature) => feature.kind === 'green')
      .sort((a, b) => b.confidence - a.confidence)[0];
    const green = greenFeature ? { lat: greenFeature.lat, lon: greenFeature.lon } : null;
    // Where the hole bends, in playing order. The bends an admin placed win outright. A hole
    // with none follows its stored playing line instead — the one the admin map draws and the
    // flyover flies — so a dogleg is not drawn, nor measured, as the chord through the trees.
    // Only a hole with neither draws the chord it always did.
    const placedTurns = forHole
      .filter((feature) => feature.kind === 'turn')
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((feature) => ({ lat: feature.lat, lon: feature.lon }));
    // The tee the view measures from: the one the hole plays longest from.
    const backTee =
      green && tees.length > 0
        ? tees.reduce((longest, tee) =>
            distanceMeters(tee, green) > distanceMeters(longest, green) ? tee : longest
          )
        : null;
    const turns =
      placedTurns.length > 0
        ? placedTurns
        : bendsAlongLine(playingLineFor(centrelines, holeNumber), backTee, green);
    // The whole course goes in and one hole comes out, which needs more than this hole's rows:
    // a hazard is given to the hole it is nearest to, and "nearest" is a comparison against
    // every other hole's mown ground and playing line. The context is that comparison's
    // material, plus the cover this hole owns outright — see `shapesNearHole`.
    const context = holeSelectionContext(shapes, centrelines, holeNumber);
    return {
      tees,
      green,
      turns,
      shapes: shapesNearHole(shapes, tees, green, context),
      // Filed under the hole by the admin who recorded the stakes, so no nearest-hole test.
      lines: lines.filter((line) => line.holeNumber === holeNumber),
    };
  }, [centrelines, features, holeNumber, lines, shapes]);

  return { ...geometry, loading };
};

/**
 * Tee and green positions for one hole of the round in play.
 *
 * The hole screen carries a round id, not a course id, so the course is resolved the same
 * way course-mapping capture resolves it. Both lookups are deferred until the view opens:
 * this is a button most players press rarely, and it should cost nothing on the holes
 * where they do not.
 *
 * Positions come from course mapping — imported geometry refined by players' GPS traces —
 * so a hole can legitimately have none, as can a whole course. That is reported as
 * `unavailable` rather than as an error, because there is nothing for the player to retry.
 */
export const useHoleViewGeometry = ({
  apiBaseUrl,
  token,
  roundId,
  holeNumber,
  enabled,
}: UseHoleViewGeometryParams): HoleViewGeometry => {
  const [lookup, setLookup] = useState<CourseLookup | null>(null);
  const resolvedCourseId = lookup && lookup.roundId === roundId ? lookup.courseId : null;
  const courseResolved = lookup !== null && lookup.roundId === roundId;

  useEffect(() => {
    if (!enabled || !roundId || !token || courseResolved) return undefined;

    let cancelled = false;
    void resolveRoundCourseId(apiBaseUrl, roundId, token).then((courseId) => {
      if (!cancelled) setLookup({ roundId, courseId });
    });

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, courseResolved, enabled, roundId, token]);

  const { loading, ...geometry } = useCourseHoleViewGeometry(
    apiBaseUrl,
    token,
    enabled ? resolvedCourseId : null,
    holeNumber
  );

  // Signing out or losing the round id leaves nothing to wait for, so those are not "loading".
  const waitingForCourse = enabled && !!roundId && !!token && !courseResolved;
  const busy = waitingForCourse || (enabled && loading);

  return {
    ...geometry,
    loading: busy,
    unavailable: enabled && !busy && geometry.green === null,
    courseId: resolvedCourseId,
  };
};
